<template>
  <BaseDialog
    :title="isShowMode ? 'Просмотр' : isEdit ? 'Редактировать' : 'Добавить'"
    :dialog="dialog"
    :is-show-mode="isShowMode"
    :is-edit="isEdit"
    :is-invalid="$v.$invalid"
    :save-btn-loading="saveBtnLoading"
    @close="closeModal"
    @save="saveEventPart"
  >
    <UITextField
      v-model="eventPart.name"
      label="Название"
      :disabled="isShowMode"
      :error-messages="errorMessages.name"
      @input="$v.eventPart.name.$touch()"
    />

    <UITextField
      v-model="eventPart.lector"
      label="Лектор"
      :disabled="isShowMode"
      :error-messages="errorMessages.lector"
      @input="$v.eventPart.lector.$touch()"
    />

    <UITextField
      v-model="eventPart.theme"
      label="Тема"
      :disabled="isShowMode"
      :error-messages="errorMessages.theme"
      @input="$v.eventPart.theme.$touch()"
    />

    <UIAutocomplete
      v-model="eventPart.eventPartType"
      label="Тип события"
      :items="eventPartTypeDirectory" item-value="id" item-text="name"
      :disabled="isShowMode"
      :error-messages="errorMessages.eventPartType" return-object
      @input="$v.eventPart.eventPartType.$touch()"
    />

    <UIAutocomplete
      v-model="eventPart.location"
      label="Площадка"
      :items="locationList.list" item-value="id" item-text="name"
      :disabled="isShowMode"
      :error-messages="errorMessages.location" return-object
      @input="$v.eventPart.location.$touch()"
    />

    <v-row class="my-0">
      <v-col :cols="isXs ? 12 : 6" class="py-0" :class="{'pr-1': !isXs}">
        <UIDatePickerMenu
          v-model="eventPart.dateFrom"
          label="Дата начала"
          :disabled="isShowMode"
          :error-messages="errorMessages.dateFrom"
          @input="$v.eventPart.dateFrom.$touch()"
        />
      </v-col>

      <v-col :cols="isXs ? 12 : 6" class="py-0" :class="{'pl-1': !isXs}">
        <UITextField
          v-model="eventPart.timeFrom"
          v-mask="'##:##'"
          label="Время начала"
          :disabled="isShowMode"
          :error-messages="errorMessages.timeFrom"
          @input="$v.eventPart.timeFrom.$touch()"
        />
      </v-col>
    </v-row>

    <v-row class="my-0">
      <v-col :cols="isXs ? 12 : 6" class="py-0" :class="{'pr-1': !isXs}">
        <UIDatePickerMenu
          v-model="eventPart.dateTo"
          label="Дата окончания"
          :disabled="isShowMode"
          :error-messages="errorMessages.dateTo"
          @input="$v.eventPart.dateTo.$touch()"
        />
      </v-col>

      <v-col :cols="isXs ? 12 : 6" class="py-0" :class="{'pl-1': !isXs}">
        <UITextField
          v-model="eventPart.timeTo"
          v-mask="'##:##'"
          label="Время окончания"
          :disabled="isShowMode"
          :error-messages="errorMessages.timeTo"
          @input="$v.eventPart.timeTo.$touch()"
        />
      </v-col>
    </v-row>
  </BaseDialog>
</template>

<script>
import {
  setValidateMessages,
  dateFormat,
  checkTimeFormat,
} from '@/scripts/validation'
import { EventPart, LocationList } from '@/models'
import {
  maxLength,
  required,
} from 'vuelidate/lib/validators'
import {
  mapActions,
  mapGetters,
} from 'vuex'
import UIAutocomplete from '@/ui/input/UIAutocomplete'
import UIDatePickerMenu from '@/ui/input/UIDatePickerMenu'
import UITextField from '@/ui/input/UITextField'
import BaseDialog from '@/ui/dialog/BaseDialog'
import { locationApi } from '@/api'

export default {
  components: {
    UIDatePickerMenu,
    UIAutocomplete,
    UITextField,
    BaseDialog,
  },

  props: {
    isShowMode: Boolean,
    saveBtnLoading: Boolean,
    dialog: Boolean,
    selectedItem: {
      type: Object,
      default: null,
    },
  },

  async mounted() {
    await this.getDirectories()
  },

  data: () => ({
    eventPart: new EventPart(),
    locationList: new LocationList()
  }),

  validations() {
    return {
      eventPart: {
        name: { required, maxLength: maxLength(200) },
        dateFrom: { required, dateFormat },
        dateTo: { required, dateFormat },
        timeFrom: { required, checkTimeFormat },
        timeTo: { required, checkTimeFormat },
        location: { required },
        lector: { required, maxLength: maxLength(200) },
        theme: { required, maxLength: maxLength(200) },
        eventPartType: { required },
      },
    }
  },

  computed: {
    ...mapGetters('directories', ['eventPartTypeDirectory']),
    ...mapGetters('breakpoint', ['isXs']),

    isEdit() {
      return !!this.selectedItem?.id
    },

    errorMessages() {
      const allErrors = {}

      Object.keys(this.$v.eventPart.$params).forEach((key) => {
        allErrors[key] = setValidateMessages(this.$v.eventPart[key])
      })

      return allErrors
    },
  },

  methods: {
    ...mapActions('directories', ['getEventPartTypeDirectory']),

    async getDirectories() {
      try {
        await this.getLocations()
        await this.getEventPartTypeDirectory()
      } catch (e) {
        this.showErrorMessage(e, 'Ошибка получения справочников')
      }
    },

    async getLocations() {
      try {
        const data = await locationApi.getLocationByEventId(this.$route.params.id)
        if (data.length) data.forEach((obj) => this.locationList.add(obj))
      } catch (e) {
        this.showErrorMessage(e, 'Ошибка получения списка локаций')
      }
    },

    saveEventPart() {
      this.eventPart.event ??= { id: Number(this.$route.params.id) }
      this.$emit('saveEventPart', this.eventPart.getForAPI())
    },

    resetModal() {
      this.eventPart = new EventPart()
      this.$v.$reset()
    },

    closeModal() {
      this.$emit('closeModal')
    },
  },

  watch: {
    selectedItem(val) {
      this.eventPart = new EventPart(val)
    },

    dialog(val) {
      !val && this.resetModal()
    },
  },
}
</script>
